import { CheckoutStatus, isCrossRegionRedirectionType, isUserLookupResponseType, isCheckoutErrorType, TrackingEvent, InitialLoadingRouteProps } from '../model'
import { UserLookupResponse } from '@afterpaytouch/portal-api/consumer/lookup/types'
import {
  resetCheckoutError,
  resetRecoverableFlowError,
  setCheckoutStatus,
  useAppDispatch,
  useAppRouter,
  useOtpLoginRouteAndTrigger,
  useRollout,
  useBiometricsEligibleAndRegisteredCheckTrigger,
  useIsEligibleBiometricLogin,
  useCheckoutToken,
  setCohort,
  useMerchantId,
  useCountry,
  useOrderSource,
} from '../state'
import { Route } from '../config/router'
import { useXbtBrowserRedirect } from './redirects'
import { OnSuccessHandler } from '../component'
import { deriveHasCashtag, evaluateCohort } from '../state/convergence/helper'
import { authService } from '@afterpaytouch/portal-api/consumer/auth'
import { useMonitoringWithEnduringEventProperties } from '../integrations/monitoring'
import { Cohort } from '@afterpaytouch/portal-api'

export const useLookupSuccessHandler: () => OnSuccessHandler = () => {
  const dispatch = useAppDispatch()
  const xbtBrowserRedirect = useXbtBrowserRedirect()
  const triggerOtpLoginIfEligibleAndReturnRoute = useOtpLoginRouteAndTrigger()
  const isEligibleBiometricLogin = useIsEligibleBiometricLogin()
  const isBiometricsEligibleAndRegisteredCheck = useBiometricsEligibleAndRegisteredCheckTrigger()
  const router = useAppRouter()
  const convergenceRollout = useRollout()
  const checkoutToken = useCheckoutToken()
  const merchantId = useMerchantId()
  const orderSource = useOrderSource()
  const { logEvent } = useMonitoringWithEnduringEventProperties()
  const country = useCountry()

  return async (result, isMobileLoginMode, email, mobile, hasNoExistingLoginIdentity = false) => {
    // Clear any previous XBT errors that might have been set.
    dispatch(resetCheckoutError())
    dispatch(resetRecoverableFlowError())
    dispatch(setCheckoutStatus(CheckoutStatus.Loaded))

    const existingCustomerNextRoute = async ({
      passwordExists,
      dateOfBirthVerificationRequired,
      trustedSession = false,
    }: UserLookupResponse): Promise<Route> => {
      if (passwordExists) {
        const isBiometricsEligibleAndRegistered = await isBiometricsEligibleAndRegisteredCheck(isEligibleBiometricLogin, isMobileLoginMode, email, mobile)
        return triggerOtpLoginIfEligibleAndReturnRoute(trustedSession, isBiometricsEligibleAndRegistered)
      }

      return isMobileLoginMode && dateOfBirthVerificationRequired ? Route.VERIFY_CHALLENGE : Route.VERIFY
    }

    const isLookup = isUserLookupResponseType(result)
    const isCrossRegion = isCrossRegionRedirectionType(result)
    const isError = isCheckoutErrorType(result)

    switch (true) {
      case isCrossRegion:
        xbtBrowserRedirect(result.crossRegionCheckoutUrl)
        break
      case isLookup: {
        const { existing, shadowAccountExists } = result
        const { cohort, theme } = evaluateCohort(
          deriveHasCashtag(result),
          existing,
          convergenceRollout,
          email,
          shadowAccountExists,
          merchantId?.toString(),
          country,
          orderSource
        )
        dispatch(setCohort(cohort))

        logEvent(TrackingEvent.ACCOUNT_LOOKUP_ATTEMPTED, {
          customerCohort: cohort,
          checkoutBrand: theme,
          shadowAccountExists,
          convergenceRollout,
          existing,
          lookupType: 'interactive',
        })

        if (result.existing) {
          const nextRoute = await existingCustomerNextRoute(result)
          router.pushQuery<InitialLoadingRouteProps>({
            pathname: Route.INITIAL_LOADING,
            query: { hasNoExistingLoginIdentity: String(hasNoExistingLoginIdentity), nextRoute, isShadowAccount: String(result.shadowAccountExists) },
          })
        } else {
          const isCashCohort = cohort === Cohort.CashOnly

          if (isCashCohort) {
            dispatch(authService.util.invalidateTags([{ type: 'CashAuth', id: checkoutToken }]))
          }
          router.pushQuery<InitialLoadingRouteProps>({
            pathname: Route.INITIAL_LOADING,
            query: { hasNoExistingLoginIdentity: String(hasNoExistingLoginIdentity), nextRoute: Route.SIGN_UP, isShadowAccount: String(shadowAccountExists) },
          })
        }
        break
      }
      case isError:
      default:
        router.push(Route.TERMINAL_ERROR)
        break
    }
  }
}
